import { Divider, Grid } from '@mui/material';
import { RoutalLoader as Loader, RoutalPalette } from '@nx-smartmonkey/ui';
import InfiniteScroll from 'react-infinite-scroll-component';
import AutoSizer from 'react-virtualized-auto-sizer';

import { getYearMonthDay } from '@nx-smartmonkey/shared/helpers';
import { useEffect, useRef, useState } from 'react';
import { wmoCodeToIconSrc } from '../../../constants/WeatherCodes';
import { Route } from '../../../domain/route/Route';
import { WeatherForecast } from '../../../domain/weather/WeatherForecast';
import { intl } from '../../../helpers/IntGlobalProvider';
import { Text } from '../../../react-components/Text';
import { RoutePill } from './RoutePill';

const SEPARATOR_ID = `today-separator`;
const CONTAINER_ID = `routes-container`;
const PREVIOUS_ID = `routes-container-previous`;

interface RoutesInfiniteLoaderProps {
  hasMoreRoutes: boolean;
  routes: Route[];
  isLoading: boolean;
  weatherForecast?: WeatherForecast[];
  loadNextPage: () => void;
  refreshList: () => void;
}

export const RoutesInfiniteLoader = ({
  hasMoreRoutes,
  routes,
  isLoading,
  weatherForecast,
  loadNextPage,
  refreshList,
}: RoutesInfiniteLoaderProps) => {
  const [routeSections, setRouteSections] = useState<Record<string, string>>({});
  const todayElement = useRef<HTMLDivElement | null>(null);
  const [firstLoad, setFirstLoad] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);

  useEffect(() => {
    let sections: Record<string, string> = {};

    for (let i = 0; i < routes.length; i++) {
      const route = routes[i];

      const date = getYearMonthDay({ date: route.plan_execution_date });

      if (Object.values(sections).includes(date)) continue;

      sections = { ...sections, [route.id]: date };
    }

    setRouteSections(sections);
  }, [routes]);

  useEffect(() => {
    if (todayElement.current && !firstLoad && routes) {
      scrollToSeparator();
      setFirstLoad(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todayElement.current, routes]);

  const scrollToSeparator = () => {
    const scrollableDiv = document.getElementById(CONTAINER_ID);
    if (scrollableDiv) {
      const todaySeparator = document.getElementById(SEPARATOR_ID);
      const previous = document.getElementById(PREVIOUS_ID);
      if (todaySeparator && previous) {
        const todaySeparatorRect = todaySeparator.getBoundingClientRect();
        const previousRect = previous.getBoundingClientRect();
        scrollableDiv.scrollTop = -(previousRect.bottom - todaySeparatorRect.top);
      }
    }
  };

  const routeHeader = ({ route }: { route: Route }) => {
    const dateRaw = routeSections[route.id];

    if (!dateRaw) {
      return null;
    }

    const date = new Date(dateRaw);
    const isToday = getYearMonthDay({ date }) === getYearMonthDay({ date: new Date() });
    const isTomorrow =
      !isToday &&
      getYearMonthDay({ date }) === getYearMonthDay({ date: new Date(new Date().setDate(new Date().getDate() + 1)) });

    let dateText = date.toLocaleDateString();
    if (isToday) {
      dateText = intl.formatMessage({ id: `drivers.route.creation.today` });
    } else if (isTomorrow) {
      dateText = intl.formatMessage({ id: `drivers.route.creation.tomorrow` });
    }

    const weather = weatherForecast?.find((w) => getYearMonthDay({ date: w.date }) === getYearMonthDay({ date }));

    return (
      <div
        ref={isToday ? todayElement : undefined}
        id={isToday ? SEPARATOR_ID : undefined}
        style={{
          boxSizing: `border-box`,
          minHeight: `40px`,
          backgroundColor: RoutalPalette.neutral00,
          padding: `10px 20px`,
          borderBottom: `1px solid ${RoutalPalette.neutral10}`,
          display: `flex`,
          flexDirection: `row`,
          justifyContent: `space-between`,
        }}
      >
        <Text variant="body">{dateText}</Text>
        {weather && (
          <Grid container wrap="nowrap" direction="row" alignItems="center" justifyContent="flex-end">
            <img src={wmoCodeToIconSrc(weather.weatherCode)} alt="weather_element" />
            <Text variant="body" style={{ width: 55, textAlign: `right`, whiteSpace: `nowrap` }}>
              {Math.round(weather?.maxTemp)}/{Math.round(weather?.minTemp)}°C
            </Text>
          </Grid>
        )}
      </div>
    );
  };

  const handleTouchEnd = () => {
    if (isScrolling) {
      setIsScrolling(false);
    }
  };

  const handleTouchMove = () => {
    if (!isScrolling) {
      setIsScrolling(true);
    }
  };

  return (
    <div
      style={{
        position: `relative`,
        height: `100%`,
        width: `100%`,
        backgroundColor: RoutalPalette.white,
      }}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchStart={handleTouchMove}
    >
      <AutoSizer>
        {({ height, width }: any) => {
          return (
            <div
              id={CONTAINER_ID}
              style={{
                height,
                width: width,
                overflow: `auto`,
                display: `flex`,
                flexDirection: `column`,
              }}
            >
              <InfiniteScroll
                dataLength={routes.length}
                pullDownToRefresh
                pullDownToRefreshContent={
                  <Grid>
                    <Loader width={20} strokeWidth={3} />
                  </Grid>
                }
                pullDownToRefreshThreshold={200}
                refreshFunction={() => {
                  setFirstLoad(false);
                  refreshList();
                }}
                next={loadNextPage}
                style={{ display: `flex`, flexDirection: `column`, overflow: `hidden` }} // To put endMessage and loader to the top.
                inverse={false}
                hasMore={hasMoreRoutes}
                loader={
                  <Grid container direction="row" alignItems="center" justifyContent="center" marginBottom="10px">
                    <Loader width={24} />
                  </Grid>
                }
                scrollableTarget={CONTAINER_ID}
              >
                <div id={PREVIOUS_ID} />
                {isLoading ? (
                  <Grid
                    container
                    direction="column"
                    alignItems="center"
                    justifyContent="center"
                    marginTop="75%"
                    padding="35px 0px"
                  >
                    <Loader width={35} />
                  </Grid>
                ) : (
                  <>
                    {routes.map((_, index) => {
                      const item = routes[index];

                      if (!(item instanceof Route)) {
                        return <div key={index}>{item}</div>;
                      }
                      return (
                        <div key={item.id}>
                          {routeHeader({ route: item })}
                          <RoutePill
                            route={item}
                            disabled={isScrolling}
                            onDelete={() => {
                              refreshList();
                            }}
                          />
                          <Divider sx={{ height: `1px`, backgroundColor: RoutalPalette.neutral10, border: `none` }} />
                        </div>
                      );
                    })}
                  </>
                )}
              </InfiniteScroll>
            </div>
          );
        }}
      </AutoSizer>
    </div>
  );
};
